/**
 * StatusManager
 *
 * Project settings section for managing custom work item statuses: rename,
 * re-categorise, reorder, archive/unarchive, delete, create new statuses
 * (optionally attached to an existing board column), and set the WIP limit
 * of the board column each status maps to. Gated on project ownership AND
 * tenant admin (FLOW-632/FLOW-646) — either alone is not enough.
 */
import { Component, For, Show, createSignal, createEffect } from "solid-js";
import {
  Button,
  Badge,
  Spinner,
  Dropdown,
  DropdownItem,
  type BadgeVariant,
} from "../ui";
import { useToast } from "../../hooks";
import {
  api,
  ApiError,
  listStatuses,
  createStatus,
  updateStatus,
  reorderStatuses,
  archiveStatus,
  unarchiveStatus,
  deleteStatus,
  updateColumn,
  type ProjectId,
  type ProjectRole,
  type TenantRole,
  type WorkItemStatus,
  type WorkItemStatusId,
  type StatusCategory,
  type BoardListResponse,
  type BoardWithColumns,
  type ColumnId,
  type ColumnWithStatuses,
  type UpdateColumnRequest,
} from "../../api";
import { canManageProject } from "../../utils/permissions";

export interface StatusManagerProps {
  projectId: ProjectId;
  role: () => ProjectRole | null;
  tenantRole: () => TenantRole | null;
}

interface ColumnEntry {
  column: ColumnWithStatuses;
  boardName: string;
}

const CATEGORY_OPTIONS: { value: StatusCategory; label: string }[] = [
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
];

const CATEGORY_BADGE: Record<StatusCategory, BadgeVariant> = {
  todo: "todo",
  in_progress: "inProgress",
  done: "done",
  cancelled: "cancelled",
};

function categoryLabel(category: StatusCategory): string {
  return (
    CATEGORY_OPTIONS.find((opt) => opt.value === category)?.label ?? category
  );
}

const DELETE_CONFLICT_FALLBACK =
  "This status is still in use — archive it instead.";

const MIN_WIP = 1;
const MAX_WIP = 999;
const RANGE_ERROR = `WIP limit must be between ${MIN_WIP} and ${MAX_WIP}`;

const asDraft = (limit: number | null | undefined) =>
  limit === null || limit === undefined ? "" : String(limit);

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

export const StatusManager: Component<StatusManagerProps> = (props) => {
  const { showError, showSuccess } = useToast();

  const [statuses, setStatuses] = createSignal<WorkItemStatus[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [loadError, setLoadError] = createSignal("");
  const [showArchived, setShowArchived] = createSignal(false);
  const [busyId, setBusyId] = createSignal<WorkItemStatusId | null>(null);
  const [rowErrors, setRowErrors] = createSignal<
    Record<WorkItemStatusId, string>
  >({});

  const [columnEntries, setColumnEntries] = createSignal<ColumnEntry[]>([]);
  const [columnsError, setColumnsError] = createSignal("");
  // Keyed by column, not by row: rows sharing a column share the edit, and a
  // row holding no draft falls through to the server value. Holding this on
  // the parent rather than in each row is what keeps one row's commit from
  // overwriting what the user is typing in another.
  const [wipDrafts, setWipDrafts] = createSignal<Record<ColumnId, string>>({});

  const [newName, setNewName] = createSignal("");
  const [newCategory, setNewCategory] = createSignal<StatusCategory>("todo");
  const [newColumnId, setNewColumnId] = createSignal("");
  const [creating, setCreating] = createSignal(false);
  const [createError, setCreateError] = createSignal("");

  const clearRowError = (id: WorkItemStatusId) => {
    setRowErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await listStatuses(props.projectId, showArchived());
      setStatuses(data.slice().sort((a, b) => a.position - b.position));
    } catch (e) {
      setLoadError(errorMessage(e, "Failed to load statuses"));
    } finally {
      setLoading(false);
    }
  };

  const loadColumns = async () => {
    setColumnsError("");
    try {
      const data = await api.get<BoardListResponse>(
        `/projects/${props.projectId}/boards`,
      );
      // The list endpoint carries no columns; each board's detail does.
      const details = await Promise.all(
        data.boards.map((board) =>
          api.get<BoardWithColumns>(`/boards/${board.id}`),
        ),
      );
      setColumnEntries(
        details.flatMap((board) =>
          board.columns.map((column) => ({ column, boardName: board.name })),
        ),
      );
    } catch (e) {
      // The WIP input hangs off this data, so a failure here silently removes
      // it from every row. Say so rather than leaving the owner to wonder.
      setColumnEntries([]);
      // Prefixed rather than replaced: the server's message is the useful
      // half, but alone ("Network request failed") it says nothing about why
      // every WIP input just disappeared.
      setColumnsError(
        `WIP limits are unavailable — ${errorMessage(e, "failed to load board columns")}`,
      );
    }
  };

  // Only the owner-gated view fetches data; re-fetch when the archived
  // toggle changes.
  createEffect(() => {
    if (!canManageProject(props.role(), props.tenantRole())) return;
    showArchived();
    void load();
  });

  createEffect(() => {
    if (!canManageProject(props.role(), props.tenantRole())) return;
    void loadColumns();
  });

  // A status maps to at most one column (there is no board-creation
  // endpoint, so a project has a single board and `column_statuses` is
  // 1:1 in practice) — the first match is the only one.
  const columnForStatus = (
    statusId: WorkItemStatusId,
  ): ColumnWithStatuses | undefined =>
    columnEntries().find((entry) => entry.column.status_ids.includes(statusId))
      ?.column;

  const otherStatusNames = (statusId: WorkItemStatusId): string[] => {
    const column = columnForStatus(statusId);
    if (!column) return [];
    const nameById = new Map(statuses().map((s) => [s.id, s.name]));
    return column.status_ids
      .filter((id) => id !== statusId)
      .map((id) => nameById.get(id))
      .filter((name): name is string => !!name);
  };

  const handleRename = async (id: WorkItemStatusId, name: string) => {
    setBusyId(id);
    clearRowError(id);
    try {
      const updated = await updateStatus(id, { name });
      setStatuses((prev) =>
        prev
          .map((s) => (s.id === id ? updated : s))
          .sort((a, b) => a.position - b.position),
      );
    } catch (e) {
      const message = errorMessage(e, "Failed to rename status");
      setRowErrors((prev) => ({ ...prev, [id]: message }));
      showError(message);
    } finally {
      setBusyId(null);
    }
  };

  const handleCategoryChange = async (
    id: WorkItemStatusId,
    category: StatusCategory,
  ) => {
    setBusyId(id);
    clearRowError(id);
    try {
      const updated = await updateStatus(id, { category });
      setStatuses((prev) =>
        prev
          .map((s) => (s.id === id ? updated : s))
          .sort((a, b) => a.position - b.position),
      );
    } catch (e) {
      const message = errorMessage(e, "Failed to change category");
      setRowErrors((prev) => ({ ...prev, [id]: message }));
      showError(message);
    } finally {
      setBusyId(null);
    }
  };

  const wipDraftFor = (column: ColumnWithStatuses | undefined) =>
    column === undefined
      ? ""
      : (wipDrafts()[column.id] ?? asDraft(column.wip_limit));

  const clearWipDraft = (columnId: ColumnId) =>
    setWipDrafts((prev) => {
      if (!(columnId in prev)) return prev;
      const next = { ...prev };
      delete next[columnId];
      return next;
    });

  const handleWipInput = (statusId: WorkItemStatusId, value: string) => {
    const column = columnForStatus(statusId);
    if (!column) return;
    setWipDrafts((prev) => ({ ...prev, [column.id]: value }));
    // Typing clears a stale range error, so restoring the saved value does not
    // leave a complaint sitting beside a field that is valid again.
    clearRowError(statusId);
  };

  const handleWipCommit = async (statusId: WorkItemStatusId) => {
    const column = columnForStatus(statusId);
    if (!column) return;

    const draft = wipDraftFor(column).trim();
    if (draft === asDraft(column.wip_limit)) {
      clearWipDraft(column.id);
      return;
    }

    let request: UpdateColumnRequest;
    if (draft === "") {
      request = { remove_wip_limit: true };
    } else {
      const parsed = Number(draft);
      if (!Number.isInteger(parsed) || parsed < MIN_WIP || parsed > MAX_WIP) {
        setRowErrors((prev) => ({ ...prev, [statusId]: RANGE_ERROR }));
        return;
      }
      request = { wip_limit: parsed };
    }

    setBusyId(statusId);
    clearRowError(statusId);
    try {
      const updated = await updateColumn(column.id, request);
      // PATCH /columns/{id} returns a bare Column — merge so status_ids,
      // which it does not carry, survives. Sibling rows read the same
      // column record, so a shared column's rows update together for free.
      setColumnEntries((prev) =>
        prev.map((entry) =>
          entry.column.id === column.id
            ? { ...entry, column: { ...entry.column, ...updated } }
            : entry,
        ),
      );
      clearWipDraft(column.id);
      showSuccess(
        updated.wip_limit === null || updated.wip_limit === undefined
          ? `WIP limit removed from ${updated.name}`
          : `WIP limit for ${updated.name} set to ${updated.wip_limit}`,
      );
    } catch (e) {
      const message = errorMessage(e, "Failed to update the WIP limit");
      setRowErrors((prev) => ({ ...prev, [statusId]: message }));
      showError(message);
    } finally {
      setBusyId(null);
    }
  };

  const handleMove = async (id: WorkItemStatusId, direction: -1 | 1) => {
    const list = statuses();
    const idx = list.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= list.length) return;

    const reordered = list.slice();
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(targetIdx, 0, moved);

    setBusyId(id);
    try {
      const updated = await reorderStatuses(props.projectId, {
        status_ids: reordered.map((s) => s.id),
      });
      setStatuses(updated.slice().sort((a, b) => a.position - b.position));
    } catch (e) {
      showError(errorMessage(e, "Failed to reorder statuses"));
    } finally {
      setBusyId(null);
    }
  };

  // Archiving or deleting the default status is refused with "set another
  // status as the default first", so the UI has to offer that move — otherwise
  // the default status is permanently un-archivable through the product.
  const handleSetDefault = async (id: WorkItemStatusId) => {
    setBusyId(id);
    clearRowError(id);
    try {
      await updateStatus(id, { is_default: true });
      await load();
      showSuccess("Default status updated");
    } catch (e) {
      const message = errorMessage(e, "Failed to set the default status");
      setRowErrors((prev) => ({ ...prev, [id]: message }));
      showError(message);
    } finally {
      setBusyId(null);
    }
  };

  const handleArchive = async (id: WorkItemStatusId) => {
    setBusyId(id);
    clearRowError(id);
    try {
      await archiveStatus(id);
      await load();
      showSuccess("Status archived");
    } catch (e) {
      // Archive is refused for the default status and for the last active
      // "done" status, and the server's reason names the fix. Pin it to the
      // row like every other failure here — a toast alone scrolls away before
      // the user can act on it.
      const message = errorMessage(e, "Failed to archive status");
      setRowErrors((prev) => ({ ...prev, [id]: message }));
      showError(message);
    } finally {
      setBusyId(null);
    }
  };

  const handleUnarchive = async (id: WorkItemStatusId) => {
    setBusyId(id);
    clearRowError(id);
    try {
      await unarchiveStatus(id);
      await load();
      showSuccess("Status unarchived");
    } catch (e) {
      const message = errorMessage(e, "Failed to unarchive status");
      setRowErrors((prev) => ({ ...prev, [id]: message }));
      showError(message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: WorkItemStatusId) => {
    setBusyId(id);
    clearRowError(id);
    try {
      await deleteStatus(id);
      await load();
      // Deleting the last status of a column deletes the column too.
      await loadColumns();
      showSuccess("Status deleted");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const message = errorMessage(e, DELETE_CONFLICT_FALLBACK);
        setRowErrors((prev) => ({ ...prev, [id]: message }));
      } else {
        showError(errorMessage(e, "Failed to delete status"));
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async () => {
    const name = newName().trim();
    if (!name) {
      setCreateError("Name is required");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      await createStatus(props.projectId, {
        name,
        category: newCategory(),
        column_id: newColumnId() || undefined,
      });
      setNewName("");
      setNewCategory("todo");
      setNewColumnId("");
      await load();
      await loadColumns();
      showSuccess("Status created");
    } catch (e) {
      const message = errorMessage(e, "Failed to create status");
      setCreateError(message);
      showError(message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Show when={canManageProject(props.role(), props.tenantRole())}>
      <section class="mb-8">
        <h2 class="text-lg font-semibold text-ink mb-4">
          Statuses & WIP Limits
        </h2>
        <div class="bg-surface rounded border border-line p-5">
          <p class="text-sm text-ink-3 mb-4">
            Customize the workflow statuses used by this project&rsquo;s boards.
            Category changes{" "}
            <strong class="font-medium text-ink-2">
              affect future transitions only
            </strong>{" "}
            — they do not rewrite the start/completion timestamps of items that
            already moved through a status. A Work-in-Progress limit warns the
            team when a board column holds too much work at once — the column
            header shows{" "}
            <strong class="font-medium text-ink-2">count / limit</strong> and
            turns amber once the limit is reached; limits are advisory and do
            not block moves. Leave the field empty for no limit.
          </p>

          <label class="flex items-center gap-2 text-sm text-ink-2 mb-4">
            <input
              type="checkbox"
              checked={showArchived()}
              onChange={(e) => setShowArchived(e.currentTarget.checked)}
            />
            Show archived statuses
          </label>

          <Show when={loading()}>
            <div class="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          </Show>

          <Show when={columnsError()}>
            <p class="text-sm text-signal-red mb-4">{columnsError()}</p>
          </Show>

          <Show when={loadError()}>
            <p class="text-sm text-signal-red mb-4">{loadError()}</p>
          </Show>

          <Show when={!loading() && !loadError()}>
            <ul class="space-y-2 mb-4">
              <For
                each={statuses()}
                fallback={<li class="text-sm text-ink-3">No statuses yet.</li>}
              >
                {(status, index) => (
                  <StatusRow
                    status={status}
                    column={columnForStatus(status.id)}
                    otherStatusNames={otherStatusNames(status.id)}
                    isFirst={index() === 0}
                    isLast={index() === statuses().length - 1}
                    busy={busyId() === status.id}
                    error={rowErrors()[status.id]}
                    onRename={(name) => handleRename(status.id, name)}
                    onCategoryChange={(category) =>
                      handleCategoryChange(status.id, category)
                    }
                    wipDraft={wipDraftFor(columnForStatus(status.id))}
                    onWipInput={(value) => handleWipInput(status.id, value)}
                    onWipCommit={() => void handleWipCommit(status.id)}
                    onMoveUp={() => handleMove(status.id, -1)}
                    onMoveDown={() => handleMove(status.id, 1)}
                    onSetDefault={() => handleSetDefault(status.id)}
                    onArchive={() => handleArchive(status.id)}
                    onUnarchive={() => handleUnarchive(status.id)}
                    onDelete={() => handleDelete(status.id)}
                  />
                )}
              </For>
            </ul>
          </Show>

          <div class="border-t border-line pt-4 mt-4">
            <h3 class="text-sm font-medium text-ink-2 mb-3">Add a Status</h3>
            <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
              <div class="flex-1 min-w-48">
                <label
                  for="new-status-name"
                  class="block text-sm font-medium text-ink-2 mb-1"
                >
                  Name
                </label>
                <input
                  id="new-status-name"
                  type="text"
                  value={newName()}
                  disabled={creating()}
                  class="block w-full rounded border border-line px-3 py-2 text-sm disabled:bg-sunken disabled:text-ink-3"
                  onInput={(e) => setNewName(e.currentTarget.value)}
                />
              </div>
              <div>
                <label
                  for="new-status-category"
                  class="block text-sm font-medium text-ink-2 mb-1"
                >
                  Category
                </label>
                <select
                  id="new-status-category"
                  value={newCategory()}
                  disabled={creating()}
                  class="block rounded border border-line px-3 py-2 text-sm"
                  onChange={(e) =>
                    setNewCategory(e.currentTarget.value as StatusCategory)
                  }
                >
                  <For each={CATEGORY_OPTIONS}>
                    {(opt) => <option value={opt.value}>{opt.label}</option>}
                  </For>
                </select>
              </div>
              <div>
                <label
                  for="new-status-column"
                  class="block text-sm font-medium text-ink-2 mb-1"
                >
                  Board column
                </label>
                <select
                  id="new-status-column"
                  value={newColumnId()}
                  disabled={creating()}
                  class="block rounded border border-line px-3 py-2 text-sm"
                  onChange={(e) => setNewColumnId(e.currentTarget.value)}
                >
                  <option value="">New column on default board</option>
                  <For each={columnEntries()}>
                    {(entry) => (
                      <option value={entry.column.id}>
                        {entry.boardName} / {entry.column.name}
                      </option>
                    )}
                  </For>
                </select>
              </div>
              <Button
                variant="secondary"
                onClick={handleCreate}
                loading={creating()}
                disabled={creating()}
              >
                Add
              </Button>
            </div>
            <Show when={createError()}>
              <p class="text-sm text-signal-red mt-2">{createError()}</p>
            </Show>
          </div>
        </div>
      </section>
    </Show>
  );
};

interface StatusRowProps {
  status: WorkItemStatus;
  column?: ColumnWithStatuses;
  otherStatusNames: string[];
  isFirst: boolean;
  isLast: boolean;
  busy: boolean;
  error?: string;
  onRename: (name: string) => void;
  onCategoryChange: (category: StatusCategory) => void;
  wipDraft: string;
  onWipInput: (value: string) => void;
  onWipCommit: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSetDefault: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onDelete: () => void;
}

const StatusRow: Component<StatusRowProps> = (props) => {
  const [name, setName] = createSignal(props.status.name);
  const isArchived = () => props.status.archived_at !== null;

  const commitRename = () => {
    const trimmed = name().trim();
    if (!trimmed) {
      setName(props.status.name);
      return;
    }
    if (trimmed !== props.status.name) {
      props.onRename(trimmed);
    }
  };

  return (
    <li
      class={
        // flex-wrap matters: the error message below is `w-full`, which in a
        // non-wrapping row just competes for width and squeezes the name input
        // to nothing. Wrapping puts the error on its own line instead.
        "flex flex-col gap-2 rounded border p-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 " +
        (isArchived() ? "border-line bg-sunken" : "border-line")
      }
    >
      {/*
        A `min-w-0` here let the name input shrink to a couple of unreadable
        characters once the badge, category select and buttons claimed the row.
        The row wraps (sm:flex-wrap above), so a floor makes those controls drop
        to the next line instead of squeezing the name.
      */}
      <div class="flex-1 min-w-48">
        <label for={`status-name-${props.status.id}`} class="sr-only">
          Name for status {props.status.name}
        </label>
        <input
          id={`status-name-${props.status.id}`}
          type="text"
          value={name()}
          disabled={props.busy || isArchived()}
          class="w-full rounded border border-line px-3 py-1.5 text-sm disabled:bg-sunken disabled:text-ink-3"
          onInput={(e) => setName(e.currentTarget.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
        />
      </div>

      <Badge variant={CATEGORY_BADGE[props.status.category]}>
        {categoryLabel(props.status.category)}
      </Badge>

      <div>
        <label for={`status-category-${props.status.id}`} class="sr-only">
          Category for status {props.status.name}
        </label>
        <select
          id={`status-category-${props.status.id}`}
          value={props.status.category}
          disabled={props.busy || isArchived()}
          class="rounded border border-line px-2 py-1.5 text-sm"
          onChange={(e) =>
            props.onCategoryChange(e.currentTarget.value as StatusCategory)
          }
        >
          <For each={CATEGORY_OPTIONS}>
            {(opt) => <option value={opt.value}>{opt.label}</option>}
          </For>
        </select>
      </div>

      <Show when={props.column}>
        <div class="flex flex-col">
          <label for={`status-wip-${props.status.id}`} class="sr-only">
            WIP limit for {props.column?.name}
          </label>
          <div class="flex items-center gap-1">
            <span class="text-xs text-ink-3">WIP</span>
            <input
              id={`status-wip-${props.status.id}`}
              type="number"
              min={MIN_WIP}
              max={MAX_WIP}
              placeholder="No limit"
              value={props.wipDraft}
              // The limit belongs to the column, not the status, so this
              // stays enabled even when the status is archived — otherwise a
              // column whose only status is archived would have no editable
              // limit anywhere.
              disabled={props.busy}
              class="w-24 rounded border border-line px-3 py-1.5 text-sm disabled:bg-sunken disabled:text-ink-3"
              onInput={(e) => props.onWipInput(e.currentTarget.value)}
              onBlur={props.onWipCommit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
            />
          </div>
          <Show when={props.otherStatusNames.length > 0}>
            <p class="w-full text-xs text-ink-3">
              Shared WIP limit with: {props.otherStatusNames.join(", ")}
            </p>
          </Show>
        </div>
      </Show>

      <div class="flex items-center gap-1.5 flex-wrap">
        <Show when={props.status.is_default}>
          <Badge variant="default">Default</Badge>
        </Show>
        <Show when={isArchived()}>
          <Badge variant="cancelled">Archived</Badge>
        </Show>
      </div>

      <div class="flex items-center gap-1">
        {/* Reordering stays inline: it is direct manipulation of the row's
            own position, and burying it two clicks deep would make moving a
            status several places a chore. The state-changing actions go in
            the menu. */}
        <Show when={!isArchived()}>
          <button
            type="button"
            class="p-1.5 rounded hover:bg-sunken disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={`Move ${props.status.name} up`}
            disabled={props.busy || props.isFirst}
            onClick={props.onMoveUp}
          >
            &uarr;
          </button>
          <button
            type="button"
            class="p-1.5 rounded hover:bg-sunken disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={`Move ${props.status.name} down`}
            disabled={props.busy || props.isLast}
            onClick={props.onMoveDown}
          >
            &darr;
          </button>
        </Show>

        <Dropdown
          align="right"
          trigger={
            <button
              type="button"
              class="p-1.5 rounded text-ink-3 hover:bg-sunken hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={`Actions for ${props.status.name}`}
              title={`Actions for ${props.status.name}`}
              disabled={props.busy}
            >
              {/* A disabled trigger swallows the click, so an in-flight row
                  cannot open its own menu — the spinner is the only feedback
                  left once the action buttons are gone. */}
              <Show when={!props.busy} fallback={<Spinner size="sm" />}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </Show>
            </button>
          }
        >
          <Show when={!isArchived() && !props.status.is_default}>
            <DropdownItem onClick={props.onSetDefault}>
              Set default
            </DropdownItem>
          </Show>
          <Show when={!isArchived()}>
            <DropdownItem onClick={props.onArchive}>Archive</DropdownItem>
          </Show>
          <Show when={isArchived()}>
            <DropdownItem onClick={props.onUnarchive}>Unarchive</DropdownItem>
          </Show>
          <DropdownItem
            class="text-signal-red hover:bg-signal-red-tint focus:bg-signal-red-tint"
            onClick={props.onDelete}
          >
            Delete
          </DropdownItem>
        </Dropdown>
      </div>

      <Show when={props.error}>
        <p class="text-sm text-signal-red w-full">{props.error}</p>
      </Show>
    </li>
  );
};
